import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { EMPTY, Observable, catchError, finalize, take } from "rxjs";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { createErrorObject } from "@webapp/error-handling/error-util";
import { KpiDetailsStateService } from "@webapp/kpis/components/kpi-details/services/kpi-details-state.service";
import { KpiProjection, KpiProjectionOpenedFrom } from "@webapp/kpis/models/kpi-projections.models";
import { Kpi } from "@webapp/kpis/models/kpis.models";
import { KpiProjectionsFacade } from "@webapp/kpis/services/kpi-projections/kpi-projections-facade.service";
import { dateValidator, isDateAfterAllowedAccessibleFutureDate } from "@webapp/kpis/utils";
import dayjs from "@webapp/shared/libs/dayjs";
import { KpiChartUtilsService } from "../kpi-details/kpi-chart/services/kpi-chart-utils.service";
import { CHART_POINT_DATE_FORMAT } from "../kpi-details/kpi-chart/utils";
import { KpiDetailsStateProxyService } from "../kpi-details/services/kpi-details-state-proxy.service";

@Component({
  selector: "kpi-projection-form",
  templateUrl: "./kpi-projection-form.component.html",
  styleUrls: ["kpi-projection-form.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiProjectionFormComponent implements OnInit {
  @Input() public kpi: Kpi;
  @Input() public projectionData: KpiProjection;
  @Input() public projectionFormOpenedFrom: KpiProjectionOpenedFrom;
  @Input() public existingProjections: KpiProjection[];
  @Output() public readonly hide: EventEmitter<void> = new EventEmitter();

  public projectionForm: FormGroup;
  public isHttpRequestInProgress = false;

  constructor(
    private formBuilder: FormBuilder,
    private kpiProjectionFacade: KpiProjectionsFacade,
    private kpiDetailsService: KpiDetailsStateService,
    private kpiChartUtilsService: KpiChartUtilsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private kpiDetailsStateProxyService: KpiDetailsStateProxyService
  ) {}

  public ngOnInit(): void {
    this.initFormData();
  }

  public onSubmit(): void {
    this.isHttpRequestInProgress = true;
    const value = <number>this.projectionForm.value.value;
    const rawDate = <Date>this.projectionForm.value.date;
    const date = dayjs(rawDate).format(CHART_POINT_DATE_FORMAT);

    if (this.projectionData.id) {
      this.patchProjection({ value, date });
    } else {
      this.createProjection({ value, date });
    }
  }

  public onDelete(): void {
    this.isHttpRequestInProgress = true;
    this.kpiChartUtilsService
      .deleteProjection$(this.kpi.id, this.projectionData.id, this.close)
      .pipe(finalize(() => (this.isHttpRequestInProgress = false)))
      .subscribe(() => {
        this.close();
        this.kpiDetailsStateProxyService.onProjectionDeleted(this.projectionData.date);
      });
  }

  public close(): void {
    this.hide.emit();
  }

  public isDateDisabled(date: Date): boolean {
    return this.isDateOfAnotherExistingProjection(date) || isDateAfterAllowedAccessibleFutureDate(date);
  }

  private isDateOfAnotherExistingProjection(date: Date): boolean {
    const existingProjectionDatesSet = new Set(
      this.existingProjections.filter((projection) => projection.id !== this.projectionData.id).map((projection) => projection.date)
    );
    return existingProjectionDatesSet.has(dayjs(date).format(CHART_POINT_DATE_FORMAT));
  }

  private initFormData(): void {
    // we have an option to get the projection data from service if editing
    const { value, date } = this.projectionData;
    this.projectionForm = this.formBuilder.group({
      value: new FormControl(value, Validators.required),
      date: new FormControl(new Date(date), [Validators.required, dateValidator.bind(this)]),
    });
  }

  private createProjection(payload: Pick<KpiProjection, "date" | "value">): void {
    this.kpiProjectionFacade
      .createProjectionForKpi$(this.kpi.id, payload, this.projectionFormOpenedFrom)
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
        finalize(() => (this.isHttpRequestInProgress = false))
      )
      .subscribe(() => {
        this.kpiDetailsService.fetchKpiProjections(this.kpi.id);
        this.close();
      });
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    this.uiErrorHandlingService.handleModal(createErrorObject(error));
    this.close();

    return EMPTY;
  }

  private patchProjection(payload: Pick<KpiProjection, "date" | "value">): void {
    this.kpiProjectionFacade
      .updateProjection$(this.kpi.id, this.projectionData.id, payload, "chart modal")
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
        finalize(() => (this.isHttpRequestInProgress = false))
      )
      .subscribe(() => {
        this.kpiDetailsStateProxyService.onProjectionUpdated({ ...this.projectionData, ...payload });
        this.kpiDetailsService.fetchKpiProjectionsAndComments(this.kpi.id);
        this.close();
      });
  }
}
